import React, { useEffect, useState } from "react";
import {
  Button,
  Flex,
  Heading,
  Spacer,
  SimpleGrid,
  VStack
} from "@chakra-ui/react";
import { AiOutlinePlus } from "react-icons/ai";
import { User } from "types/user";
import UsersDataTable from "./UsersDataTable";
import UserForm from "./UserForm";
import {
  createUser,
  deleteUser,
  fetchClientUsers,
  updateUser
} from "api/webservice/users";
import { AuthenticatedLayout } from "layouts";
import { useToast } from "hooks";
import { useModal } from "context/modal";
import { ca } from "date-fns/locale";

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [newUserId, setNewUserId] = useState("");
  const { openModal, closeModal, setTitle, setContent } = useModal()!;
  const toast = useToast();

  interface RefreshUserTableProps {
    newUserId: string;
  }

  const refreshUsersTable = ({ newUserId }: RefreshUserTableProps): void => {
    handleFetchUsers();
    setNewUserId(newUserId);
  };
  const handleFetchUsers = (): void => {
    fetchClientUsers().then((response: User[]) => {
      if (response) setUsers(response);
    });
  };

  const handleCreateUser = (user: User): void => {
    createUser({ ...user })
      .then(createdUser => {
        closeModal();
        refreshUsersTable({
          newUserId: createdUser ? createdUser.id : ""
        });
      })
      .catch(error => {
        toast({
          title: "Error creating client",
          description: error.response.data.response.errors[0].message,
          status: "error"
        });
      });
  };

  const handleUpdateUser = (user: User): void => {
    updateUser({ ...user })
      .then(updatedUser => {
        refreshUsersTable({
          newUserId: updatedUser ? updatedUser.id : ""
        });
      })
      .catch(error => {
        toast({
          title: "Error updating client",
          description: error.response.data.response.errors[0].message,
          status: "error"
        });
      });
  };

  const handleDeleteUser = (user: User): void => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${user.first_name} ${user.last_name}? \n (this action can be undone)`
      )
    ) {
      return;
    }

    closeModal();
    deleteUser({ ...user })
      .then(() => {
        refreshUsersTable({
          newUserId: user ? user.id : ""
        });
        toast({
          title: "Successfully deleted user",
          description: "",
          status: "success"
        });
      })
      .catch(error => {
        toast({
          title: "Error deleting user",
          status: "error"
        });
      });
  };

  const openCreateModal = () => {
    setTitle("Create New User");
    setContent(
      <UserForm saveUser={handleCreateUser} deleteUser={handleDeleteUser} />
    );
    openModal();
  };

  useEffect(() => {
    handleFetchUsers();
  }, [setUsers]);

  return (
    <AuthenticatedLayout>
      <VStack alignItems="flex-start" spacing={8}>
        <SimpleGrid columns={3} w="100%">
          <Heading size="lg">Client Management</Heading>
          <Spacer />
          <Flex alignItems="center" justifyContent="flex-end">
            <Button
              size="md"
              variant="blue"
              rounded="full"
              iconSpacing={0}
              leftIcon={<AiOutlinePlus />}
              onClick={openCreateModal}
            >
              Add Client
            </Button>
          </Flex>
        </SimpleGrid>
        <UsersDataTable
          users={users}
          highlightUserId={newUserId}
          saveUser={handleUpdateUser}
          deleteUser={handleDeleteUser}
        />
      </VStack>
    </AuthenticatedLayout>
  );
};

export default Users;
